// client/src/components/pages/LexiStar/Replay/replayLogic.ts
//
// Pure logic for replay (LS-09 §5.6; FR-RPL-001/002/004/005/013; m3-contracts
// §7.3 GET /replay): response normalisation, the client-side clock's
// interpolation, track slicing by time (stage colouring, deviating segments),
// progressive loading (which windows of the simplified track to refetch at
// full resolution, and how to splice them in), chart downsampling and
// transport-control helpers.
//
// Times are epoch milliseconds internally; the API sends ISO instants.
//
// ST-00245: positionAt() is a pure function of (track, t) — scrubbing forwards,
// backwards, before the first fix or after the last gives the same answer as
// playing up to that time. Between two fixes far apart in time it tells a stop
// (no displacement: hold the place and the heading, 0 km/h) from a long move
// (average speed over the span, heading along it), and it reports where the
// clock is relative to the fixes (`motion`) so the page can say so.
// Converted from replayLogic.js in the same change (CLAUDE.md TypeScript rule).

import type { Feature, FeatureCollection, Geometry, LineString } from 'geojson';
import { lerpAngle as lerpAngleJs } from '../../../map/geo';
import {
  assetStatusMarker,
  assetStatusVar,
  statusKeyOfStage,
  type AssetStatusKey,
} from '../../../map/assetStatus';
import type {
  FuelRow,
  ReplayData,
  ReplayDeviation,
  ReplayEvent,
  ReplayGeometryLike,
  ReplayInstant,
  ReplayPlannedDto,
  ReplayPosition,
  ReplayResponse,
  ReplaySpan,
  ReplaySpanDto,
  SpeedRow,
  StageBand,
  TrackPoint,
} from '../../../../types/lexistar-replay';

export type {
  ReplayData,
  ReplayEvent,
  ReplayPosition,
  TrackPoint,
} from '../../../../types/lexistar-replay';

const lerpAngle = lerpAngleJs as unknown as (a: number, b: number, t: number) => number;

export const SPEEDS = [1, 2, 4, 8, 16, 32, 64];
export const LOW_TOLERANCE_M = 25;
export const DETAIL_ZOOM = 12;
export const MAX_DETAIL_WINDOWS = 3;
export const CHART_MAX_POINTS = 1500;

/** Two fixes at least this far apart in time … */
export const STATIONARY_MIN_MS = 2 * 60000;
/** … and closer than this (just past the 25 m simplification tolerance) are a stop. */
export const STATIONARY_MAX_M = 30;
/** Past this span the earlier fix's speed is stale: report the span's average. */
export const LONG_SPAN_MS = 10 * 60000;
/** Below this displacement a bearing between two fixes is noise. */
const BEARING_MIN_M = 5;
/** How far back positionAt looks for the last known heading. */
const HEADING_LOOKBACK = 200;

/** ISO string | epoch s | epoch ms | Date → epoch ms (NaN when unreadable). */
export function toMs(v: ReplayInstant | unknown): number {
  if (v === null || v === undefined || v === '') return NaN;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  if (/^\d+(\.\d+)?$/.test(String(v))) return toMs(Number(v));
  return Date.parse(String(v));
}

const isNum = (v: unknown): boolean =>
  v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));
const numOrNull = (v: unknown): number | null => (isNum(v) ? Number(v) : null);
const byT = (a: { t: number }, b: { t: number }) => a.t - b.t;
const list = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

/** A span row ({from,to} under any of the usual names) → {from, to} in ms plus the row. */
function span(row: ReplaySpanDto, fromKeys: string[], toKeys: string[]): ReplaySpan {
  const from = fromKeys.map((k) => toMs(row[k])).find((x) => Number.isFinite(x));
  const to = toKeys.map((k) => toMs(row[k])).find((x) => Number.isFinite(x));
  return { ...row, from: from ?? NaN, to: to ?? from ?? NaN };
}

const FROM = ['from', 'start', 'started_at', 'entered_at', 'opened_at', 'at'];
const TO = ['to', 'end', 'ended_at', 'exited_at', 'closed_at'];

/** The GET /replay body, normalised and sorted. */
export function normaliseReplay(res: ReplayResponse = {}): ReplayData {
  const track: TrackPoint[] = list(res.track)
    .map((p) => ({
      t: toMs(p.t ?? p.ts ?? p.ts_device),
      lat: Number(p.lat),
      lon: Number(p.lon),
      speed_kmh: numOrNull(p.speed_kmh ?? p.speed),
      heading: numOrNull(p.heading),
      fuel_pct: numOrNull(p.fuel_pct),
      ignition: p.ignition ?? null,
    }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.lat) && Number.isFinite(p.lon))
    .sort(byT);
  const events: ReplayEvent[] = list(res.events)
    .map((e) => ({ ...e, t: toMs(e.event_time ?? e.t ?? e.at) }))
    .filter((e) => Number.isFinite(e.t))
    .sort(byT);
  const spans = (rows: ReplaySpanDto[] | undefined): ReplaySpan[] =>
    list(rows)
      .map((r) => span(r, FROM, TO))
      .filter((r) => Number.isFinite(r.from))
      .sort((a, b) => a.from - b.from);
  const deviations: ReplayDeviation[] = spans(res.deviations).map((d) => ({
    ...d,
    approved: d.approved === true || String(d.state || '').toUpperCase() === 'APPROVED',
  }));
  const series = <T extends object>(
    rows: ReplaySpanDto[] | undefined,
    map: (r: ReplaySpanDto) => T
  ): (T & { t: number })[] =>
    list(rows)
      .map((r) => ({ ...map(r), t: toMs(r.t ?? r.ts) }))
      .filter((r) => Number.isFinite(r.t))
      .sort(byT);
  const trips = list(res.trips);
  const scope = res.scope || {};
  return {
    asset_key: res.asset_key || scope.asset_key || res.trip?.asset_key || null,
    asset: res.asset || null,
    trip:
      res.trip ||
      (scope.kind === 'trip'
        ? trips.find((t) => t.id === scope.trip_id) || trips[0] || null
        : null),
    trips,
    tolerance_m: numOrNull(res.tolerance_m ?? scope.tolerance_m),
    truncated: !!res.truncated,
    next_from: res.next_from || null,
    stats: res.stats || null,
    track,
    events,
    stage_bands: spans(res.stage_bands) as StageBand[],
    stops: spans(res.stops),
    idles: spans(res.idles),
    visits: spans(res.visits),
    deviations,
    planned: res.planned || null,
    planned_legs: list(res.planned_legs),
    fuel_series: series(res.fuel_series, (r) => ({
      level_pct: numOrNull(r.level_pct),
      used_l: numOrNull(r.used_l),
    })) as FuelRow[],
    speed_series: series(res.speed_series, (r) => ({
      kmh: numOrNull(r.kmh ?? r.speed_kmh),
      limit: numOrNull(r.limit ?? r.limit_kmh),
    })) as SpeedRow[],
  };
}

/** [start, end] of everything the replay shows, or null when there is nothing. */
export function timeBounds(
  r: Pick<ReplayData, 'track' | 'stage_bands' | 'events'>,
  hint: { from?: ReplayInstant; to?: ReplayInstant } = {}
): { start: number; end: number } | null {
  const ts = [
    r.track[0]?.t,
    r.track[r.track.length - 1]?.t,
    r.stage_bands[0]?.from,
    r.stage_bands[r.stage_bands.length - 1]?.to,
    r.events[0]?.t,
    r.events[r.events.length - 1]?.t,
    toMs(hint.from),
    toMs(hint.to),
  ].filter((x): x is number => Number.isFinite(x));
  if (!ts.length) return null;
  const start = Math.min(...ts);
  const end = Math.max(...ts);
  return end > start ? { start, end } : { start, end: start + 60000 };
}

/** Index of the last point at or before t (−1 before the first). */
export function indexAt(track: { t: number }[], t: number): number {
  let lo = 0;
  let hi = track.length - 1;
  if (hi < 0 || t < track[0]!.t) return -1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (track[mid]!.t <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

// ── Geometry ───────────────────────────────────────────────────────────────

const EARTH_RADIUS_M = 6371008.8;
const RAD = Math.PI / 180;

/** Great-circle distance in metres between two {lat, lon}. */
export function distanceM(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number }
): number {
  const dLat = (b.lat - a.lat) * RAD;
  const dLon = (b.lon - a.lon) * RAD;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing from a to b, degrees clockwise from north in [0, 360). */
export function bearingDeg(
  a: { lat: number; lon: number },
  b: { lat: number; lon: number }
): number {
  const y = Math.sin((b.lon - a.lon) * RAD) * Math.cos(b.lat * RAD);
  const x =
    Math.cos(a.lat * RAD) * Math.sin(b.lat * RAD) -
    Math.sin(a.lat * RAD) * Math.cos(b.lat * RAD) * Math.cos((b.lon - a.lon) * RAD);
  return (((Math.atan2(y, x) / RAD) % 360) + 360) % 360;
}

const norm360 = (deg: number) => ((deg % 360) + 360) % 360;

/** The last heading known at or before fix i: a reported heading, or the bearing of a move. */
function headingBefore(track: TrackPoint[], i: number): number | null {
  const stop = Math.max(0, i - HEADING_LOOKBACK);
  for (let j = i; j >= stop; j--) {
    const p = track[j]!;
    if (p.heading !== null) return p.heading;
    const q = track[j - 1];
    if (q && distanceM(q, p) >= BEARING_MIN_M) return bearingDeg(q, p);
  }
  return null;
}

/** The first heading known at or after fix i (for the time before the first fix). */
function headingAfter(track: TrackPoint[], i: number): number | null {
  const stop = Math.min(track.length - 1, i + HEADING_LOOKBACK);
  for (let j = i; j <= stop; j++) {
    const p = track[j]!;
    if (p.heading !== null) return p.heading;
    const q = track[j + 1];
    if (q && distanceM(p, q) >= BEARING_MIN_M) return bearingDeg(p, q);
  }
  return null;
}

/**
 * The truck at time t (ST-00245). Pure in (track, t): the answer never depends on
 * where the clock was before, so a scrub backwards, a jump or a replay from the
 * start all land on the same place.
 *  - before the first fix / after the last: that fix, `motion` 'before' / 'after',
 *    no speed (nothing was measured then);
 *  - between two fixes at least STATIONARY_MIN_MS apart that are within
 *    STATIONARY_MAX_M of each other: a stop — the place and heading held, 0 km/h
 *    (the simplified track is time-synchronised, so a long parked spell is two
 *    fixes in one place);
 *  - otherwise linearly interpolated, heading the short way round (or along the
 *    span when a fix has none, or the span is long); speed of the earlier fix, or
 *    the span's average once it is longer than LONG_SPAN_MS.
 */
export function positionAt(track: TrackPoint[], t: number): ReplayPosition | null {
  if (!track.length || !Number.isFinite(t)) return null;
  const n = track.length;
  const first = track[0]!;
  const last = track[n - 1]!;
  if (t < first.t) {
    return {
      ...first,
      t,
      speed_kmh: null,
      heading: first.heading ?? headingAfter(track, 0),
      motion: 'before',
      fix_from: first.t,
      fix_to: first.t,
    };
  }
  if (t > last.t) {
    return {
      ...last,
      t,
      speed_kmh: null,
      heading: last.heading ?? headingBefore(track, n - 1),
      motion: 'after',
      fix_from: last.t,
      fix_to: last.t,
    };
  }
  const i = indexAt(track, t);
  if (i >= n - 1) {
    return {
      ...last,
      t,
      heading: last.heading ?? headingBefore(track, n - 1),
      motion: 'moving',
      fix_from: last.t,
      fix_to: last.t,
    };
  }
  const a = track[i]!;
  const b = track[i + 1]!;
  const dt = b.t - a.t;
  const f = dt > 0 ? (t - a.t) / dt : 0;
  const dist = distanceM(a, b);
  const stationary = dt >= STATIONARY_MIN_MS && dist < STATIONARY_MAX_M;
  let heading: number | null;
  // Standing still: hold the heading it arrived with (or, before its first move,
  // the way it will leave) — never spin it slowly across the stop.
  if (stationary) heading = a.heading ?? headingBefore(track, i) ?? headingAfter(track, i + 1);
  else if (dist >= BEARING_MIN_M && (dt > LONG_SPAN_MS || a.heading === null || b.heading === null))
    heading = bearingDeg(a, b);
  else if (a.heading !== null && b.heading !== null)
    heading = norm360(lerpAngle(a.heading, b.heading, f));
  else heading = a.heading ?? b.heading ?? headingBefore(track, i);
  let speed: number | null;
  if (stationary) speed = 0;
  else if (dt > LONG_SPAN_MS) speed = Math.round((dist / dt) * 3600 * 10) / 10;
  else speed = a.speed_kmh;
  return {
    t,
    lat: a.lat + (b.lat - a.lat) * f,
    lon: a.lon + (b.lon - a.lon) * f,
    heading,
    speed_kmh: speed,
    fuel_pct: a.fuel_pct,
    ignition: a.ignition,
    motion: stationary ? 'stationary' : 'moving',
    fix_from: a.t,
    fix_to: b.t,
  };
}

/** [lng, lat] coordinates of the track between t0 and t1, with interpolated ends. */
export function sliceTrack(track: TrackPoint[], t0: number, t1: number): [number, number][] {
  if (!track.length || !(t1 > t0)) return [];
  const coords: [number, number][] = [];
  const first = positionAt(track, Math.max(t0, track[0]!.t));
  if (first) coords.push([first.lon, first.lat]);
  const i0 = indexAt(track, t0) + 1;
  for (let i = Math.max(0, i0); i < track.length && track[i]!.t < t1; i++) {
    if (track[i]!.t > t0) coords.push([track[i]!.lon, track[i]!.lat]);
  }
  const last = positionAt(track, Math.min(t1, track[track.length - 1]!.t));
  if (last) coords.push([last.lon, last.lat]);
  return coords.length >= 2 ? coords : [];
}

/**
 * A trip stage's status key (map/assetStatus): the key the control room shows for a truck in
 * that stage, so the track, the Stage lane and the marker match the Status Board and Live View
 * (ST-00253 / ST-00244), by the rules (map/assetVisualRules). No stage (no trip) is "parked".
 */
export const stageKey = (stage: string | null | undefined): AssetStatusKey => statusKeyOfStage(stage);

/** A stage's map colour (the track by stage, the marker). */
export const stageColor = (stage: string | null | undefined): string =>
  assetStatusMarker(stageKey(stage));

/** A stage's colour on the app surface (the Stage lane, legend swatches): theme-aware. */
export const stageSurfaceColor = (stage: string | null | undefined): string =>
  assetStatusVar(stageKey(stage), 'solid');

type LineFeature = Feature<LineString, Record<string, unknown>>;

const line = (
  coords: [number, number][],
  properties: Record<string, unknown>
): LineFeature | null =>
  coords.length >= 2
    ? { type: 'Feature', properties, geometry: { type: 'LineString', coordinates: coords } }
    : null;

const fc = <G extends Geometry>(
  features: (Feature<G, Record<string, unknown>> | null)[]
): FeatureCollection<G, Record<string, unknown>> => ({
  type: 'FeatureCollection',
  features: features.filter((f): f is Feature<G, Record<string, unknown>> => f !== null),
});

/** The whole track (drawn underneath) plus one coloured slice per stage band. */
export function trackFeatures(r: Pick<ReplayData, 'track' | 'stage_bands'>) {
  const all = line(
    r.track.map((p) => [p.lon, p.lat]),
    { kind: 'track', color: '#64748b' }
  );
  const bands = r.stage_bands.map((b) =>
    line(sliceTrack(r.track, b.from, b.to), {
      kind: 'stage',
      stage: b.stage,
      color: stageColor(b.stage),
    })
  );
  return { base: fc([all]), stages: fc(bands) };
}

/** Deviating segments of the actual track: approved (dashed) vs unapproved (solid). */
export function deviationFeatures(r: Pick<ReplayData, 'track' | 'deviations'>) {
  return fc(
    r.deviations.map((d) =>
      line(
        sliceTrack(r.track, d.from, Number.isFinite(d.to) && d.to > d.from ? d.to : d.from + 1),
        {
          id: d.id,
          approved: !!d.approved,
        }
      )
    )
  );
}

type AnyFeature = Feature<Geometry, Record<string, unknown>>;

/**
 * Planned route line(s) and corridor(s) as features. Every leg with a pinned
 * route is drawn (a re-planned trip shows both plans: replaced legs carry
 * `replaced: true` and are drawn dashed); without legs, the single `planned`.
 */
export function plannedFeatures(
  planned: ReplayPlannedDto | null | undefined,
  legs: ReplayPlannedDto[] = []
) {
  const g = (x: ReplayGeometryLike | null | undefined): AnyFeature | null => {
    if (!x) return null;
    if (x.type === 'Feature') return x as unknown as AnyFeature;
    if (x.type === 'FeatureCollection') return (x.features?.[0] as unknown as AnyFeature) || null;
    if (x.type && x.coordinates)
      return { type: 'Feature', properties: {}, geometry: x as unknown as Geometry };
    if (x.geometry) return g(x.geometry);
    return null;
  };
  const withProps = (f: AnyFeature | null, props: Record<string, unknown>): AnyFeature | null =>
    f ? { ...f, properties: { ...(f.properties || {}), ...props } } : null;
  const source = legs.length ? legs : planned ? [planned] : [];
  const routes = source
    .map((l) => withProps(g(l.geometry), { replaced: !!l.replaced, seq: l.seq ?? null }))
    .filter((f): f is AnyFeature => f !== null);
  const corridors = source
    .filter((l) => !l.replaced)
    .map((l) => g(l.corridor))
    .filter((f): f is AnyFeature => f !== null);
  return {
    route: routes.find((f) => !f.properties.replaced) || routes[0] || null,
    routes: fc(routes),
    corridor: corridors[0] || null,
    corridors: fc(corridors),
    replanned: routes.some((f) => f.properties.replaced),
  };
}

export type EventPoint = ReplayEvent & { lat: number; lon: number };

/** Event markers on the map: events with a position (or placed on the track at their time). */
export function eventPoints(r: Pick<ReplayData, 'track' | 'events'>): EventPoint[] {
  return r.events
    .map((e): EventPoint | null => {
      if (isNum(e.lat) && isNum(e.lon)) return { ...e, lat: Number(e.lat), lon: Number(e.lon) };
      const p = positionAt(r.track, e.t);
      return p ? { ...e, lat: p.lat, lon: p.lon } : null;
    })
    .filter((e): e is EventPoint => e !== null);
}

// ── Transport controls ─────────────────────────────────────────────────────

export function nextEventTime(events: { t: number }[], t: number): number | null {
  const e = events.find((x) => x.t > t + 500);
  return e ? e.t : null;
}

export function prevEventTime(events: { t: number }[], t: number): number | null {
  for (let i = events.length - 1; i >= 0; i--) if (events[i]!.t < t - 500) return events[i]!.t;
  return null;
}

/** One sample back / forward on the track (at least a second). */
export function stepTime(track: { t: number }[], t: number, dir: number): number {
  if (!track.length) return t;
  const i = indexAt(track, t);
  if (dir < 0) {
    const cur = track[i];
    const j = cur && cur.t < t - 1 ? i : i - 1;
    return j >= 0 ? track[j]!.t : track[0]!.t;
  }
  const j = Math.min(track.length - 1, i + 1);
  return track[j]!.t > t ? track[j]!.t : Math.min(track[track.length - 1]!.t, t + 1000);
}

/** Advance the clock: real elapsed ms × speed, clamped to the end. */
export function advance(t: number, elapsedMs: number, speed: number, end: number): number {
  return Math.min(end, t + Math.max(0, elapsedMs) * speed);
}

// ── Progressive loading (FR-RPL-013) ───────────────────────────────────────

/**
 * Time windows of the simplified track that fall inside the visible map
 * bounds — one per contiguous run of visible points, padded with the points
 * either side so the refetched detail joins up. Windows already loaded are
 * dropped; at most MAX_DETAIL_WINDOWS are returned.
 * @param track the (simplified) track
 * @param bounds [[w,s],[e,n]]
 * @param loaded [[t0,t1]] already at full resolution
 */
export function detailWindows(
  track: TrackPoint[],
  bounds: [[number, number], [number, number]] | null | undefined,
  loaded: [number, number][] = []
): [number, number][] {
  if (!track.length || !bounds) return [];
  const [[w, s], [e, n]] = bounds;
  const inside = (p: TrackPoint) => p.lon >= w && p.lon <= e && p.lat >= s && p.lat <= n;
  const runs: [number, number][] = [];
  let start: number | null = null;
  track.forEach((p, i) => {
    if (inside(p)) {
      if (start === null) start = i;
    } else if (start !== null) {
      runs.push([start, i - 1]);
      start = null;
    }
  });
  if (start !== null) runs.push([start, track.length - 1]);
  // A run whose own points already lie in a loaded window is done (after a
  // splice its padding neighbours move, so the padded span is not the test).
  const covered = (t0: number, t1: number) => loaded.some(([a, b]) => a <= t0 && b >= t1);
  return runs
    .filter(([i0, i1]) => !covered(track[i0]!.t, track[i1]!.t))
    .map(([i0, i1]): [number, number] => [
      track[Math.max(0, i0 - 1)]!.t,
      track[Math.min(track.length - 1, i1 + 1)]!.t,
    ])
    .filter(([t0, t1]) => t1 > t0)
    .slice(0, MAX_DETAIL_WINDOWS);
}

/** Splice full-resolution points for [t0, t1] into the track. */
export function mergeDetail<P extends { t: number }>(
  track: P[],
  detail: P[],
  [t0, t1]: [number, number]
): P[] {
  const outside = track.filter((p) => p.t < t0 || p.t > t1);
  const within = detail.filter((p) => p.t >= t0 && p.t <= t1);
  return [...outside, ...within].sort(byT);
}

// ── Charts ─────────────────────────────────────────────────────────────────

/**
 * At most `max` points for a chart. Buckets keep their peak `keepMax` field
 * (so a speed spike never vanishes) and the rest from the bucket's last row.
 */
export function downsample<R extends { t: number }>(
  rows: R[],
  max: number = CHART_MAX_POINTS,
  keepMax: (keyof R & string) | null = null
): R[] {
  if (rows.length <= max) return rows;
  const size = Math.ceil(rows.length / max);
  const out: R[] = [];
  for (let i = 0; i < rows.length; i += size) {
    const bucket = rows.slice(i, i + size);
    const last = bucket[bucket.length - 1]!;
    if (keepMax) {
      const peak = bucket.reduce<R | null>(
        (m, r) =>
          isNum(r[keepMax]) && (m === null || Number(r[keepMax]) > Number(m[keepMax])) ? r : m,
        null
      );
      out.push(peak ? { ...last, [keepMax]: peak[keepMax], t: peak.t } : last);
    } else {
      out.push(last);
    }
  }
  return out;
}

/** Speed series for the chart: the API's series, or derived from the track. */
export function speedRows(r: Pick<ReplayData, 'speed_series' | 'track'>): SpeedRow[] {
  const rows: SpeedRow[] = r.speed_series.length
    ? r.speed_series
    : r.track
        .filter((p) => p.speed_kmh !== null)
        .map((p) => ({ t: p.t, kmh: p.speed_kmh, limit: null }));
  return downsample(rows, CHART_MAX_POINTS, 'kmh');
}

export function fuelRows(r: Pick<ReplayData, 'fuel_series' | 'track'>): FuelRow[] {
  const rows: FuelRow[] = r.fuel_series.length
    ? r.fuel_series
    : r.track
        .filter((p) => p.fuel_pct !== null)
        .map((p) => ({ t: p.t, level_pct: p.fuel_pct, used_l: null }));
  return downsample(rows, CHART_MAX_POINTS);
}

/** One-line chart summaries (the charts' text equivalent). */
export function speedSummary(rows: { kmh?: number | null; limit?: number | null }[]): string {
  const speeds = rows.filter((x) => isNum(x.kmh));
  if (!speeds.length) return 'No speed data.';
  const max = speeds.reduce((m, x) => (Number(x.kmh) > Number(m.kmh) ? x : m));
  const over = rows.filter(
    (x) => isNum(x.kmh) && isNum(x.limit) && Number(x.kmh) > Number(x.limit)
  ).length;
  const limit = isNum(max.limit) ? ` (limit ${Math.round(Number(max.limit))})` : '';
  return `Max ${Math.round(Number(max.kmh))} km/h${limit}${over ? ` · ${over} sample${over === 1 ? '' : 's'} over the limit` : ''}.`;
}

export function fuelSummary(rows: { level_pct?: number | null; used_l?: number | null }[]): string {
  const used = rows.filter((x) => isNum(x.used_l));
  const levels = rows.filter((x) => isNum(x.level_pct));
  if (!used.length && !levels.length) return 'No fuel data.';
  const parts: string[] = [];
  if (used.length) parts.push(`${Math.round(Number(used[used.length - 1]!.used_l))} L used`);
  if (levels.length) {
    parts.push(
      `level ${Math.round(Number(levels[0]!.level_pct))}% → ${Math.round(Number(levels[levels.length - 1]!.level_pct))}%`
    );
  }
  return `${parts.join(' · ')}.`;
}

// ── Formatting ─────────────────────────────────────────────────────────────

const TZ = 'Africa/Johannesburg';
const SAST_OFFSET_MS = 2 * 3600000; // UTC+2, no DST
const DAY_MS = 24 * 3600000;

// ST-00277: one formatter each, made once. toLocale*String builds a new Intl formatter on every
// call, which a 25-truck replay (lane titles, list rows, 15 frames a second) felt; the output is
// the same.
const TIME_HMS = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});
const TIME_HM = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});
const DATE_TIME = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});
const DATE_DM = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, day: '2-digit', month: 'short' });

export const clockTime = (ms: number | null | undefined, withSeconds = true): string =>
  Number.isFinite(ms) ? (withSeconds ? TIME_HMS : TIME_HM).format(ms as number) : '—';

export const clockDateTime = (ms: number | null | undefined): string =>
  Number.isFinite(ms) ? DATE_TIME.format(ms as number) : '—';

/** "04 Oct" (SAST). */
export const clockDate = (ms: number): string => DATE_DM.format(ms);

/** Same SAST calendar day? */
export const sameSastDay = (a: number, b: number): boolean =>
  Math.floor((a + SAST_OFFSET_MS) / DAY_MS) === Math.floor((b + SAST_OFFSET_MS) / DAY_MS);

/**
 * An axis tick's label: the date at SAST midnight ("04 Oct"), the time otherwise
 * ("14:00") — so a multi-day window reads as days, not a row of "00:00".
 */
export const tickLabel = (t: number): string =>
  (t + SAST_OFFSET_MS) % DAY_MS === 0 ? clockDate(t) : clockTime(t, false);

/** "16:01", or "03 Oct 16:01" when `ms` is not on the same SAST day as `ref`. */
export const clockNear = (ms: number, ref: number): string =>
  sameSastDay(ms, ref) ? clockTime(ms, false) : `${clockDate(ms)} ${clockTime(ms, false)}`;

/** Axis ticks: whole hours (or half/quarter hours on short windows), SAST. */
export function timeTicks(start: number, end: number, target = 8): number[] {
  const span = end - start;
  if (!(span > 0)) return [];
  const steps = [5, 10, 15, 30, 60, 120, 180, 240, 360, 720, 1440].map((m) => m * 60000);
  const step = steps.find((s) => span / s <= target) || steps[steps.length - 1]!;
  // Align to SAST (UTC+2, no DST) boundaries.
  const first = Math.ceil((start + SAST_OFFSET_MS) / step) * step - SAST_OFFSET_MS;
  const ticks: number[] = [];
  for (let t = first; t <= end; t += step) ticks.push(t);
  return ticks;
}

/** "YYYY-MM-DDTHH:MM" (the date-time picker's local value) ↔ ISO. */
const pad = (n: number) => String(n).padStart(2, '0');
export function localInputValue(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function isoFromLocalInput(v: string | null | undefined): string {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

export const assetKeyOf = (
  asset:
    | {
        asset_key?: string | null;
        registration_normalised?: string | null;
        registration?: string | null;
      }
    | null
    | undefined
): string =>
  asset?.asset_key ||
  (asset?.registration_normalised || asset?.registration
    ? `reg:${String(asset.registration_normalised || asset.registration)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')}`
    : '');

export const registrationOf = (assetKey: string | null | undefined): string =>
  String(assetKey || '').replace(/^(reg|asset):/, '');
